/**
 * 批次動作的執行結果。批次動作逐本執行、遇錯即停，因此結果要能表達
 * 「做到哪裡」而不只是成功或失敗。
 */
export type BulkOutcome = {
  /** 實際完成的本數。 */
  completed: number
  /** 已完成作品的識別碼，呼叫端據此把它們從選取移除。 */
  completedIds: string[]
  /** 停止原因。全部成功時是空字串。 */
  reason: string
  /**
   * 原始例外。訊息轉成字串之後就分不出登入失效與單本資料有問題，
   * 而備份佇列正是靠這個差別決定要繼續下一本還是暫停整批。
   */
  cause?: unknown
}

export function errorText(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause)
}

/**
 * 逐本執行一個動作，第一次失敗即停止。
 *
 * 不跳過失敗繼續：批次動作失敗的常見成因是系統性的（雲端登入過期、資料庫寫入被拒），
 * 逐本重試只會把同一個錯誤重複 N 次，而使用者要看的是一次原因。
 */
export async function runSequentially(
  ids: readonly string[], action: (id: string) => Promise<void>,
): Promise<BulkOutcome> {
  const completedIds: string[] = []
  for (const id of ids) {
    try { await action(id) }
    catch (cause) {
      return { completed: completedIds.length, completedIds, reason: errorText(cause), cause }
    }
    completedIds.push(id)
  }
  return { completed: completedIds.length, completedIds, reason: '' }
}

/**
 * 以固定的並行度逐項執行，第一個失敗即停止並拋出。
 *
 * 上傳原本用 `Promise.all` 把整本的頁面一次全開。資料夾作品沒事——那裡的每一頁是
 * 磁碟上的懶惰 File 參照，不佔記憶體；但壓縮檔的每一頁都要真的解壓成 Blob，於是
 * 一本 615 頁的壓縮檔會同時產生 615 個解壓中的 Blob，把分頁的記憶體吃光。
 *
 * 並行度不需要大：轉檔本身已經限制在兩個 worker，因此再多的並行只是讓更多來源
 * Blob 提早躺在記憶體裡等著，不會更快。
 */
export async function runBounded<T>(
  items: readonly T[], limit: number, run: (item: T) => Promise<void>,
): Promise<void> {
  if (!items.length) return
  const width = Math.max(1, Math.min(limit, items.length))
  let next = 0
  let failed = false
  let failure: unknown
  const worker = async () => {
    while (!failed) {
      const index = next
      next += 1
      if (index >= items.length) return
      try { await run(items[index]) }
      catch (cause) {
        if (!failed) { failed = true; failure = cause }
        return
      }
    }
  }
  await Promise.all(Array.from({ length: width }, () => worker()))
  if (failed) throw failure
}
